/**
 * Chart color palette backed by CSS custom properties defined in index.css.
 *
 * All values use the `rgb(var(--chart-*))` pattern so they work with
 * Tailwind opacity modifiers and Recharts fill/stroke props alike.
 *
 * Categorical slots (`chart1`–`chart10`) are for distinguishing data
 * series. Semantic slots (`positive`, `negative`, `warning`) carry
 * meaning. `accent` and `muted` are general-purpose highlights.
 */

// ---------------------------------------------------------------------------
// Categorical palette (10 distinguishable hues)
// ---------------------------------------------------------------------------

export const chartColors = {
    chart1: "rgb(var(--chart-1))",
    chart2: "rgb(var(--chart-2))",
    chart3: "rgb(var(--chart-3))",
    chart4: "rgb(var(--chart-4))",
    chart5: "rgb(var(--chart-5))",
    chart6: "rgb(var(--chart-6))",
    chart7: "rgb(var(--chart-7))",
    chart8: "rgb(var(--chart-8))",
    chart9: "rgb(var(--chart-9))",
    chart10: "rgb(var(--chart-10))",
} as const;

// ---------------------------------------------------------------------------
// Semantic colors
// ---------------------------------------------------------------------------

export const chartSemanticColors = {
    positive: "rgb(var(--chart-positive))",
    negative: "rgb(var(--chart-negative))",
    warning: "rgb(var(--chart-warning))",
    accent: "rgb(var(--chart-accent))",
    accentSubtle: "rgb(var(--chart-accent-subtle))",
    muted: "rgb(var(--chart-muted))",
    others: "rgb(var(--chart-others))",
    /** Primary content color for axis labels that shouldn't use a chart hue. */
    axisText: "rgb(var(--content-primary))",
    /** Ona tool blue — used in AI Adoption tool breakdown and Usage charts. */
    toolOna: "rgb(var(--chart-tool-ona))",
    /** PRs Merged purple — used in Velocity and Usage charts. */
    velocityPr: "rgb(var(--chart-velocity-pr))",
    /** Human only grey — used in AI Adoption tool breakdown and Usage charts. */
    toolHuman: "rgb(var(--chart-tool-human))",
} as const;

// ---------------------------------------------------------------------------
// Ordered array for programmatic access (e.g. mapping N series)
// ---------------------------------------------------------------------------

/** Categorical colors in order, for mapping an index to a color. */
export const chartColorScale: readonly string[] = Object.values(chartColors);

// ---------------------------------------------------------------------------
// CSS variable names (for use in Tailwind arbitrary values or ChartConfig)
// ---------------------------------------------------------------------------

/** CSS variable reference for a categorical slot (1-based). */
export const chartCssVar = (slot: number): string => `rgb(var(--chart-${slot}))`;
